/**
 * POS Sales Filters
 *
 * Filter bar for POS sales control page.
 * Includes product selects with deep linking for inspection tabs.
 */

import { useState, useMemo } from 'react';
import { X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button, SearchAutocomplete, DatePicker, Select, parseDateParam, formatDateParam } from '@/components/ui';
import type { SearchAutocompleteOption, DateRange } from '@/components/ui';
import { useDebounce } from '@/hooks';
import { posProductsService } from '@/features/pos-products/services/pos-products.service';
import { rawProductsService } from '@/features/raw-products/services/raw-products.service';
import type { CancelStatus, SaleType } from '@bstock/shared';

/** Valeur du filtre hasMovements (US-4 Sprint 21) */
export type HasMovementsFilter = '' | 'true' | 'false';

interface PosSalesFiltersProps {
  // Date range filter
  startDate: string;
  endDate: string;
  onDateRangeChange: (startDate: string, endDate: string) => void;

  // Cancel status
  cancelStatus: CancelStatus;
  onCancelStatusChange: (status: CancelStatus) => void;

  // Search
  search: string;
  onSearchChange: (search: string) => void;

  // Sale types
  saleTypes: SaleType[];
  onSaleTypesChange: (types: SaleType[]) => void;

  // Product filters (deep linked - only IDs go to URL)
  posProductId: string;
  onPosProductIdChange: (id: string) => void;
  rawProductId: string;
  onRawProductIdChange: (id: string) => void;

  // Reset
  onReset: () => void;
  hasActiveFilters: boolean;

  // Show sale types filter (only on pos-products tab)
  showSaleTypes?: boolean;

  // Movements filter (US-4 Sprint 21)
  hasMovements?: HasMovementsFilter;
  onHasMovementsChange?: (value: HasMovementsFilter) => void;
  showMovementsFilter?: boolean;
}

const SALE_TYPE_OPTIONS: { value: SaleType; label: string }[] = [
  { value: 'STANDARD', label: 'Standard' },
  { value: 'PROMO', label: 'Promo' },
  { value: 'EMPLOYEE_MEAL', label: 'Repas employe' },
  { value: 'OFFERED', label: 'Offert' },
];

const CANCEL_STATUS_OPTIONS: { value: CancelStatus; label: string }[] = [
  { value: 'all', label: 'Tous' },
  { value: 'exclude-cancelled', label: 'Hors annules' },
  { value: 'cancelled-only', label: 'Annules seuls' },
];

const HAS_MOVEMENTS_OPTIONS: { value: HasMovementsFilter; label: string }[] = [
  { value: '', label: 'Tous' },
  { value: 'true', label: 'Avec mouvements' },
  { value: 'false', label: 'Sans mouvements' },
];

export function PosSalesFilters({
  startDate,
  endDate,
  onDateRangeChange,
  cancelStatus,
  onCancelStatusChange,
  search,
  onSearchChange,
  saleTypes,
  onSaleTypesChange,
  posProductId,
  onPosProductIdChange,
  rawProductId,
  onRawProductIdChange,
  onReset,
  hasActiveFilters,
  showSaleTypes = false,
  hasMovements = '',
  onHasMovementsChange,
  showMovementsFilter = false,
}: PosSalesFiltersProps) {
  // Local state for search inputs (not in URL)
  const [posProductSearch, setPosProductSearch] = useState('');
  const [rawProductSearch, setRawProductSearch] = useState('');

  // Debounce product searches
  const debouncedPosProductSearch = useDebounce(posProductSearch, 300);
  const debouncedRawProductSearch = useDebounce(rawProductSearch, 300);

  // Search POS products
  const { data: posProductsData, isLoading: isSearchingPosProducts } = useQuery({
    queryKey: ['pos-products', 'search', debouncedPosProductSearch],
    queryFn: () => posProductsService.list({ search: debouncedPosProductSearch, limit: 15, page: 1 }),
    enabled: debouncedPosProductSearch.length >= 2,
    staleTime: 30 * 1000,
  });

  // Search raw products
  const { data: rawProductsData, isLoading: isSearchingRawProducts } = useQuery({
    queryKey: ['raw-products', 'search', debouncedRawProductSearch],
    queryFn: () => rawProductsService.list({ search: debouncedRawProductSearch, limit: 15, page: 1 }),
    enabled: debouncedRawProductSearch.length >= 2,
    staleTime: 30 * 1000,
  });

  // Fetch selected POS product details (for display when ID is in URL)
  const { data: selectedPosProduct } = useQuery({
    queryKey: ['pos-products', posProductId],
    queryFn: () => posProductsService.getById(posProductId),
    enabled: !!posProductId,
    staleTime: 60 * 1000,
  });

  // Fetch selected raw product details (for display when ID is in URL)
  const { data: selectedRawProduct } = useQuery({
    queryKey: ['raw-products', rawProductId],
    queryFn: () => rawProductsService.getById(rawProductId),
    enabled: !!rawProductId,
    staleTime: 60 * 1000,
  });

  // Map to autocomplete options
  const posProductOptions: SearchAutocompleteOption[] = useMemo(() => {
    return (posProductsData?.items ?? []).map(product => ({
      id: product.id,
      label: product.name,
      sublabel: product.posCategory || undefined,
    }));
  }, [posProductsData]);

  const rawProductOptions: SearchAutocompleteOption[] = useMemo(() => {
    return (rawProductsData?.data ?? []).map(product => ({
      id: product.id,
      label: product.name,
      sublabel: product.sku,
    }));
  }, [rawProductsData]);

  // Current selected values for autocomplete
  // Use fetched details if available, otherwise show loading placeholder while query runs
  const posProductValue: SearchAutocompleteOption | null = useMemo(() => {
    if (!posProductId) return null;
    if (selectedPosProduct) {
      return {
        id: selectedPosProduct.id,
        label: selectedPosProduct.name,
        sublabel: selectedPosProduct.posCategory || undefined,
      };
    }
    // Show placeholder while loading (ID is set but details not yet fetched)
    return { id: posProductId, label: 'Chargement...' };
  }, [posProductId, selectedPosProduct]);

  const rawProductValue: SearchAutocompleteOption | null = useMemo(() => {
    if (!rawProductId) return null;
    if (selectedRawProduct) {
      return {
        id: selectedRawProduct.id,
        label: selectedRawProduct.name,
        sublabel: selectedRawProduct.sku,
      };
    }
    // Show placeholder while loading (ID is set but details not yet fetched)
    return { id: rawProductId, label: 'Chargement...' };
  }, [rawProductId, selectedRawProduct]);

  // Convert string dates to DateRange for DatePicker
  const dateRangeValue: DateRange = useMemo(() => ({
    start: parseDateParam(startDate),
    end: parseDateParam(endDate),
  }), [startDate, endDate]);

  // Handle DateRange change from DatePicker
  const handleDateRangeChange = (range: DateRange) => {
    const startStr = formatDateParam(range.start);
    const endStr = formatDateParam(range.end ?? range.start);
    if (startStr && endStr) {
      onDateRangeChange(startStr, endStr);
    } else if (startStr) {
      onDateRangeChange(startStr, startStr);
    }
  };

  // Handlers
  const handlePosProductChange = (option: SearchAutocompleteOption | null) => {
    onPosProductIdChange(option?.id ?? '');
  };

  const handleRawProductChange = (option: SearchAutocompleteOption | null) => {
    onRawProductIdChange(option?.id ?? '');
  };

  const toggleSaleType = (type: SaleType) => {
    if (saleTypes.includes(type)) {
      onSaleTypesChange(saleTypes.filter((t) => t !== type));
    } else {
      onSaleTypesChange([...saleTypes, type]);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm space-y-4">
      {/* Main row: Grouped filters with responsive layout */}
      <div className="flex flex-wrap items-end gap-4">
        {/* Group 1: Dates */}
        <div className="flex items-end gap-2">
          <div className="w-72">
            <label className="block text-xs font-medium text-gray-500 mb-1">Periode</label>
            <DatePicker
              showTime
              value={dateRangeValue}
              onChange={handleDateRangeChange}
              placeholder="Selectionner une periode"
            />
          </div>
        </div>

        {/* Group 2: Search */}
        <div className="flex-1 min-w-[200px] max-w-[320px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">Recherche</label>
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="N° de commande"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Group 3: Filters metier */}
        <div className="flex items-end gap-2 flex-wrap">
          <div className="w-40">
            <label className="block text-xs font-medium text-gray-500 mb-1">Statut annulation</label>
            <Select
              value={cancelStatus}
              onChange={(value) => onCancelStatusChange(value as CancelStatus)}
              options={CANCEL_STATUS_OPTIONS}
              allowEmpty={false}
              placeholder="Statut annulation"
            />
          </div>

          {showMovementsFilter && onHasMovementsChange && (
            <div className="w-44">
              <label className="block text-xs font-medium text-gray-500 mb-1">Mouvements stock</label>
              <Select
                value={hasMovements}
                onChange={(value) => onHasMovementsChange(value as HasMovementsFilter)}
                options={HAS_MOVEMENTS_OPTIONS}
                allowEmpty={false}
                placeholder="Mouvements stock"
              />
            </div>
          )}

          <div className="w-64">
            <label className="block text-xs font-medium text-gray-500 mb-1">Produit fini</label>
            <SearchAutocomplete
              placeholder="Filtrer par produit fini"
              value={posProductValue}
              onChange={handlePosProductChange}
              options={posProductOptions}
              isLoading={isSearchingPosProducts}
              searchValue={posProductSearch}
              onSearchChange={setPosProductSearch}
              emptyMessage="Aucun produit POS trouve"
              minSearchLength={2}
            />
          </div>

          <div className="w-64">
            <label className="block text-xs font-medium text-gray-500 mb-1">Produit brut</label>
            <SearchAutocomplete
              placeholder="Filtrer par produit brut"
              value={rawProductValue}
              onChange={handleRawProductChange}
              options={rawProductOptions}
              isLoading={isSearchingRawProducts}
              searchValue={rawProductSearch}
              onSearchChange={setRawProductSearch}
              emptyMessage="Aucun produit brut trouve"
              minSearchLength={2}
            />
          </div>
        </div>

        {/* Reset button */}
        {hasActiveFilters && (
          <div className="flex items-end ml-auto">
            <Button variant="ghost" size="sm" onClick={onReset}>
              <X className="w-4 h-4 mr-1" />
              Reset
            </Button>
          </div>
        )}
      </div>

      {/* Second row: Sale Types (only on pos-products tab) */}
      {showSaleTypes && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-2">
            Types de vente
          </label>
          <div className="flex flex-wrap gap-2">
            {SALE_TYPE_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => toggleSaleType(option.value)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  saleTypes.length === 0 || saleTypes.includes(option.value)
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
